import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, languages } from '@/i18n/translations';

const SettingsContext = createContext();

const defaultSettings = {
  language: 'pt',
  theme: 'light',
  pin: null,
  pinEnabled: false,
  biometricEnabled: false, // Face ID / Touch ID
  biometricCredentialId: null, // WebAuthn credential ID
  catalogPdfUrl: null,
  defaultPanelSizes: ['1920x1005', '2400x1005', '2400x1200', '1200x1200', '2400x50'],
};

// Check if WebAuthn (Face ID/Touch ID) is available
const checkBiometricAvailable = async () => {
  if (!window.PublicKeyCredential) return false;
  try {
    const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    return available;
  } catch {
    return false;
  }
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('gresart_settings');
    return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
  });

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Default to read-only mode (not authenticated) unless explicitly logged in
    return localStorage.getItem('gresart_authenticated') === 'true';
  });
  const [biometricAvailable, setBiometricAvailable] = useState(false);

  useEffect(() => {
    localStorage.setItem('gresart_settings', JSON.stringify(settings));
    
    // Apply theme
    if (settings.theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [settings]);

  // Check biometric availability on mount
  useEffect(() => {
    checkBiometricAvailable().then(setBiometricAvailable);
  }, []);

  // Translation function
  const t = useCallback((key) => {
    const lang = settings.language || 'pt';
    return translations[lang]?.[key] || translations['pt']?.[key] || key;
  }, [settings.language]);

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const setPin = (newPin) => {
    setSettings(prev => ({ 
      ...prev, 
      pin: newPin,
      pinEnabled: !!newPin 
    }));
  };

  const removePin = () => {
    setSettings(prev => ({
      ...prev,
      pin: null,
      pinEnabled: false,
      biometricEnabled: false,
      biometricCredentialId: null
    }));
    setIsAuthenticated(false);
  };

  const verifyPin = (inputPin) => {
    if (!settings.pinEnabled || !settings.pin) {
      return true;
    }
    const isValid = inputPin === settings.pin;
    if (isValid) {
      setIsAuthenticated(true);
    }
    return isValid;
  };

  const requiresPin = () => {
    return settings.pinEnabled && settings.pin && !isAuthenticated;
  };

  // Enable biometric authentication (Face ID / Touch ID)
  const enableBiometric = async () => {
    if (!biometricAvailable || !settings.pinEnabled) return false;
    
    try {
      // Create a credential for this app
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: new Uint8Array(32),
          rp: { name: "Gresart Showroom Control", id: window.location.hostname },
          user: {
            id: new Uint8Array(16),
            name: "gresart_user",
            displayName: "Utilizador Gresart"
          },
          pubKeyCredParams: [
            { type: "public-key", alg: -7 },  // ES256
            { type: "public-key", alg: -257 } // RS256
          ],
          authenticatorSelection: {
            authenticatorAttachment: "platform",
            userVerification: "required"
          },
          timeout: 60000
        }
      });

      if (credential) {
        const credentialId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
        setSettings(prev => ({
          ...prev,
          biometricEnabled: true,
          biometricCredentialId: credentialId
        }));
        return true;
      }
    } catch (error) {
      console.error('Biometric setup error:', error);
    }
    return false;
  };

  // Disable biometric
  const disableBiometric = () => {
    setSettings(prev => ({
      ...prev,
      biometricEnabled: false,
      biometricCredentialId: null
    }));
  };

  // Verify using biometric (Face ID / Touch ID)
  const verifyBiometric = async () => {
    if (!settings.biometricEnabled || !settings.biometricCredentialId) return false;
    
    try {
      const credentialId = Uint8Array.from(atob(settings.biometricCredentialId), c => c.charCodeAt(0));
      
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: new Uint8Array(32),
          allowCredentials: [{
            type: "public-key",
            id: credentialId,
            transports: ["internal"]
          }],
          userVerification: "required",
          timeout: 60000
        }
      });

      if (assertion) {
        setIsAuthenticated(true);
        return true;
      }
    } catch (error) {
      console.error('Biometric verification error:', error);
    }
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('gresart_authenticated');
  };

  const login = () => {
    setIsAuthenticated(true);
    localStorage.setItem('gresart_authenticated', 'true');
  };

  // "Read-only mode" used to be this app's only access gate before mandatory email/password
  // login existed. Now that every screen already requires a real backend session
  // (see AuthContext/ProtectedRoute), that separate local device toggle is redundant and was
  // confusing users ("Entrar" showing again right after a real login) - so it's retired here.
  // Any authenticated real user can always edit; `login`/`logout`/`isAuthenticated` above are
  // kept only for the unrelated "Secção Privada" PIN vault feature (PrivateSection.jsx).
  const isReadOnly = false;

  const clearCache = () => {
    const settingsBackup = localStorage.getItem('gresart_settings');
    localStorage.clear();
    if (settingsBackup) {
      localStorage.setItem('gresart_settings', settingsBackup);
    }
  };

  return (
    <SettingsContext.Provider value={{
      settings,
      updateSetting,
      setPin,
      removePin,
      verifyPin,
      requiresPin,
      isAuthenticated,
      setIsAuthenticated,
      logout,
      login,
      isReadOnly,
      clearCache,
      t,
      languages,
      // Biometric
      biometricAvailable,
      enableBiometric,
      disableBiometric,
      verifyBiometric,
    }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
